import { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLazyQuery } from '@apollo/client';
import { GET_TASK_POOL, QUOTE_FROM_HASH } from '../queries';
import { CoreTaskStatusChoices, GetTaskPoolQuery } from '../gql/graphql';
import { StoreState } from '../store';
import QuoteWizardState from '../store/QuoteWizardState';
import { resetAxisState } from '../store/axisStore';
import { usePrevious } from './usePrevious';

const POLLING_INTERVAL_MS = 500;

export interface UseAxisQuoteSubmissionOptions {
  active?: boolean;
  onComplete?: () => void;
  resetStateOnComplete?: boolean | (() => boolean);
}

type TaskPool = NonNullable<GetTaskPoolQuery['getTaskPool']>;

const isTaskPoolFinished = (taskPool: TaskPool) => {
  if (!taskPool.tasks || taskPool.tasks.length === 0) {
    return false;
  }

  return taskPool.tasks.every((task) => {
    if (!task) {
      return false;
    }

    return (
      task.status === CoreTaskStatusChoices.Completed ||
      task.status === CoreTaskStatusChoices.Failed
    );
  });
};

const parseTaskPoolAdditionalInformation = (
  taskPool: TaskPool,
): Record<string, unknown> | null => {
  if (!taskPool.additionalInformation) {
    return null;
  }

  try {
    const additionalInfo = JSON.parse(taskPool.additionalInformation);

    if (!additionalInfo || typeof additionalInfo !== 'object') {
      return null;
    }

    return additionalInfo;
  } catch {
    return null;
  }
};

const getQuoteUuidFromTaskPool = (taskPool: TaskPool): string | null => {
  const additionalInfo = parseTaskPoolAdditionalInformation(taskPool);
  const quoteUuid = additionalInfo?.quote_uuid;

  if (typeof quoteUuid === 'string' && quoteUuid.trim() !== '') {
    return quoteUuid;
  }

  return null;
};

const getQuoteCorrespondenceHashFromTaskPool = (
  taskPool: TaskPool,
): string | null => {
  const additionalInfo = parseTaskPoolAdditionalInformation(taskPool);
  const quoteCorrespondenceHash = additionalInfo?.quote_correspondence_hash;

  if (
    typeof quoteCorrespondenceHash === 'string' &&
    quoteCorrespondenceHash.trim() !== ''
  ) {
    return quoteCorrespondenceHash;
  }

  return null;
};

export function useAxisQuoteSubmission({
  active = true,
  onComplete,
  resetStateOnComplete = true,
}: UseAxisQuoteSubmissionOptions = {}) {
  const dispatch = useDispatch();
  const taskPoolId = useSelector(
    (state: StoreState) => state.quoteWizard.quoteSubmissionTaskPoolId,
  );
  const previousTaskPoolId = usePrevious(taskPoolId);
  const hasCompletedRef = useRef(false);
  const awaitingQuoteFromHashRef = useRef(false);
  const storedQuoteUuidRef = useRef<string | null>(null);

  const [
    getTaskPool,
    {
      data: taskPoolData,
      error: taskPoolError,
      loading: taskPoolLoading,
      stopPolling,
      startPolling,
      called,
    },
  ] = useLazyQuery(GET_TASK_POOL, {
    pollInterval: POLLING_INTERVAL_MS,
  });

  const [
    getQuoteFromHash,
    { data: quoteHashData, error: quoteError, loading: quoteLoading },
  ] = useLazyQuery(QUOTE_FROM_HASH);

  const storeQuoteUuidIfNeeded = useCallback(
    (quoteUuid: string | null) => {
      if (!quoteUuid || storedQuoteUuidRef.current === quoteUuid) {
        return;
      }

      storedQuoteUuidRef.current = quoteUuid;
      dispatch(
        QuoteWizardState.actions.setCurrentQuoteUUID({
          uuid: quoteUuid,
        }),
      );
    },
    [dispatch],
  );

  const completeSubmission = useCallback(() => {
    if (hasCompletedRef.current) {
      return;
    }

    hasCompletedRef.current = true;
    awaitingQuoteFromHashRef.current = false;
    stopPolling?.();
    dispatch(QuoteWizardState.actions.cancelAllSubmissions());
    dispatch(QuoteWizardState.actions.setQuoteSubmissionTaskPoolId(null));

    const shouldResetState =
      typeof resetStateOnComplete === 'function'
        ? resetStateOnComplete()
        : resetStateOnComplete !== false;

    if (shouldResetState) {
      dispatch(QuoteWizardState.actions.resetSelections(['questionsPerPage']));
      dispatch(resetAxisState());
    }

    if (onComplete) {
      onComplete();
    }
  }, [dispatch, onComplete, resetStateOnComplete, stopPolling]);

  useEffect(() => {
    hasCompletedRef.current = false;
    awaitingQuoteFromHashRef.current = false;
    storedQuoteUuidRef.current = null;
  }, [taskPoolId]);

  useEffect(() => {
    if (!active) {
      stopPolling?.();
      return;
    }

    const hasNewTaskPoolId = taskPoolId && taskPoolId !== previousTaskPoolId;

    if (hasNewTaskPoolId) {
      getTaskPool({
        variables: { taskPoolId },
      });
      startPolling?.(POLLING_INTERVAL_MS);
    } else if (!taskPoolId && previousTaskPoolId) {
      stopPolling?.();
    }

    return () => {
      stopPolling?.();
    };
  }, [
    taskPoolId,
    previousTaskPoolId,
    getTaskPool,
    startPolling,
    stopPolling,
    active,
  ]);

  useEffect(() => {
    const taskPool = taskPoolData?.getTaskPool;
    const hasError = taskPoolError || quoteError || taskPool?.error;

    if (hasError && called) {
      stopPolling?.();
    }
  }, [
    taskPoolError,
    quoteError,
    taskPoolData?.getTaskPool?.error,
    called,
    stopPolling,
  ]);

  useEffect(() => {
    if (!active) {
      return;
    }

    const taskPool = taskPoolData?.getTaskPool;
    if (!taskPool) {
      return;
    }

    storeQuoteUuidIfNeeded(getQuoteUuidFromTaskPool(taskPool));
  }, [taskPoolData, active, storeQuoteUuidIfNeeded]);

  useEffect(() => {
    if (
      !active ||
      hasCompletedRef.current ||
      awaitingQuoteFromHashRef.current
    ) {
      return;
    }

    const taskPool = taskPoolData?.getTaskPool;
    if (!taskPool || !isTaskPoolFinished(taskPool)) {
      return;
    }

    if (storedQuoteUuidRef.current) {
      completeSubmission();
      return;
    }

    const quoteCorrespondenceHash =
      getQuoteCorrespondenceHashFromTaskPool(taskPool);
    if (quoteCorrespondenceHash) {
      awaitingQuoteFromHashRef.current = true;
      getQuoteFromHash({
        variables: {
          hash: quoteCorrespondenceHash,
          delete: false,
        },
      });
      return;
    }

    completeSubmission();
  }, [taskPoolData, active, getQuoteFromHash, completeSubmission]);

  useEffect(() => {
    if (
      !active ||
      !awaitingQuoteFromHashRef.current ||
      hasCompletedRef.current
    ) {
      return;
    }

    const quoteData = quoteHashData?.quoteFromHash;
    if (!quoteData?.uniqueID) {
      return;
    }

    const quoteUuid =
      typeof quoteData.uniqueID === 'string'
        ? quoteData.uniqueID
        : String(quoteData.uniqueID);

    storeQuoteUuidIfNeeded(quoteUuid);
    completeSubmission();
  }, [quoteHashData, active, storeQuoteUuidIfNeeded, completeSubmission]);

  return {
    taskPool: {
      loading: taskPoolLoading,
      error: taskPoolError,
      data: taskPoolData,
    },
    quote: {
      loading: quoteLoading,
      error: quoteError,
      data: quoteHashData,
    },
  };
}
